import {
  applyQuickCreateStoryboardOverride,
  createQuickCreateStoryboard,
} from '@tiny-fox/storybook-layout';
import type { QuickCreateStoryboard, SeriesTextBox } from '@tiny-fox/storybook-layout';
import {
  PageCompositionStrategy,
  PageCompositionTreatment,
  QuickCreateComposition,
  StructuredPlotData
} from '../types/storybook';

export const DEFAULT_QUICK_CREATE_COMPOSITION: QuickCreateComposition = {
  mode: 'auto-book-rhythm',
  textDensity: 'balanced',
  treatment: 'paper-fade',
  pageOverrides: {}
};

export interface QuickCreateStoryboardPage {
  pageId: string;
  pageIndex: number;
  totalWords: number;
  hasText: boolean;
  requestedStrategy: PageCompositionStrategy;
  requestedTreatment: PageCompositionTreatment;
  strategy: PageCompositionStrategy;
  treatment: PageCompositionTreatment;
  preferredCandidateId: string;
  status: 'safe-preview' | 'requires-image-analysis';
  safeguard?: string;
  regenerateText: false;
  regenerateArt: false;
  providerCallsRequired: 0;
}

export interface QuickCreateStoryboardView extends Omit<QuickCreateStoryboard, 'pages'> {
  pages: QuickCreateStoryboardPage[];
}

export const resolveQuickCreateComposition = (
  value?: Partial<QuickCreateComposition>
): QuickCreateComposition => ({
  mode: value?.mode || DEFAULT_QUICK_CREATE_COMPOSITION.mode,
  textDensity: value?.textDensity || DEFAULT_QUICK_CREATE_COMPOSITION.textDensity,
  treatment: value?.treatment || DEFAULT_QUICK_CREATE_COMPOSITION.treatment,
  pageOverrides: value?.pageOverrides || {},
  ...(value?.storyboardHash ? { storyboardHash: value.storyboardHash } : {})
});

export const buildQuickCreateStoryboard = (
  outline: StructuredPlotData | undefined,
  composition?: Partial<QuickCreateComposition>
): QuickCreateStoryboardView => {
  const controls = resolveQuickCreateComposition(composition);
  return createQuickCreateStoryboard({
    pages: (outline?.pages || []).map((page, index) => {
      const id = page.outlinePageId || `outline-${index + 1}`;
      return {
        id,
        textBoxes: (page.textBoxes || []) as SeriesTextBox[],
        hasIllustration: true,
        ...(controls.pageOverrides[id]
          ? { compositionOverride: controls.pageOverrides[id] }
          : {})
      };
    }),
    controls: {
      compositionMode: controls.mode,
      textDensity: controls.textDensity,
      treatment: controls.treatment
    }
  }) as QuickCreateStoryboardView;
};

const defaultTreatmentForStrategy = (
  strategy: PageCompositionStrategy,
  bookTreatment: QuickCreateComposition['treatment']
): PageCompositionTreatment => {
  if (strategy === 'image-only' || strategy === 'quiet-zone') return 'integrated';
  if (strategy === 'illustrated-text-page') return 'solid-paper';
  if (strategy === 'split-vignette') return bookTreatment === 'solid-paper' ? 'solid-paper' : 'framed-vignette';
  return bookTreatment;
};

/**
 * Runs the shared fail-safe override guard before returning anything that may
 * be persisted. This operation is pure and never calls an image or text model.
 */
export const updateQuickCreatePageOverride = (
  outline: StructuredPlotData | undefined,
  composition: QuickCreateComposition,
  pageId: string,
  strategy: PageCompositionStrategy,
  treatment?: PageCompositionTreatment
): QuickCreateComposition => {
  const storyboard = buildQuickCreateStoryboard(outline, composition);
  const guarded = applyQuickCreateStoryboardOverride(storyboard, pageId, {
    strategy,
    treatment: treatment || defaultTreatmentForStrategy(strategy, composition.treatment)
  }) as QuickCreateStoryboardView;
  const page = guarded.pages.find(candidate => candidate.pageId === pageId);
  if (!page) throw new Error(`Storyboard page not found: ${pageId}`);
  return {
    ...composition,
    pageOverrides: {
      ...composition.pageOverrides,
      [pageId]: {
        strategy: page.requestedStrategy,
        treatment: page.requestedTreatment
      }
    }
  };
};

export const clearQuickCreatePageOverride = (
  composition: QuickCreateComposition,
  pageId: string
): QuickCreateComposition => {
  const pageOverrides = { ...composition.pageOverrides };
  delete pageOverrides[pageId];
  return { ...composition, pageOverrides };
};

export const treatmentsForPageStrategy = (
  strategy: PageCompositionStrategy
): PageCompositionTreatment[] => {
  if (strategy === 'image-only' || strategy === 'quiet-zone') return ['integrated'];
  if (strategy === 'illustrated-text-page') return ['solid-paper'];
  if (strategy === 'split-vignette') return ['framed-vignette', 'solid-paper'];
  return ['paper-fade', 'solid-paper'];
};
